/**
 * Minimum-version guard (D19, §R.107–§R.128).
 *
 * Two mechanisms, one screen:
 *
 *   1. The FLOOR — `app_settings.min_supported_app_version.android`, read by
 *      src/hooks/useMinSupportedVersion.ts and compared against the Android
 *      versionCode (`platform.app.version().build`). Strictly `build < floor`.
 *      An UNKNOWN floor fails OPEN (the offline shell is a promise); a KNOWN
 *      floor the build is below fails CLOSED, at launch and at the call site.
 *
 *   2. The SIGNAL — a server refusal carrying SQLSTATE `KA001` in
 *      `error.code`, observed by src/lib/updateRequiredInterceptor.ts on every
 *      PostgREST response. That is the enforcement; the floor is an affordance.
 *
 * `ensureVersionSupported()` is the call-site trigger ((d) in the brief).
 * M1 ships it and proves it blocks; the milestone that adds a
 * compatibility-sensitive RPC wires it and lists the RPC below.
 *
 * Pure module: no React, no platform import. The shell configures it.
 */

export type UpdateRequiredReason = 'below-floor' | 'server-refused' | 'build-withdrawn';

/**
 * RPCs that must not be sent by a build below the floor. Each call site awaits
 * ensureVersionSupported() first. start_booking is the first (M7, §R.119): the
 * arrival marker it will one day require exists only in M7 builds.
 */
export const COMPATIBILITY_SENSITIVE_CALLS: readonly string[] = ['start_booking'];

export class UpdateRequiredError extends Error {
  constructor(public readonly reason: UpdateRequiredReason) {
    super(
      reason === 'below-floor'
        ? 'This build is below the minimum supported version'
        : reason === 'build-withdrawn'
          ? 'This build has been withdrawn'
          : 'The server requires an app update',
    );
    this.name = 'UpdateRequiredError';
  }
}

/** A build ops have withdrawn by its versionCode (§G M9 (e)). Unknown build or list → not withdrawn. */
export function isWithdrawnBuild(build: number | null | undefined, withdrawn: readonly number[] | null | undefined): boolean {
  return build != null && !!withdrawn && withdrawn.includes(build);
}

/** Strict: a build AT the floor is the oldest supported build (§1 of the brief). */
export function isBelowFloor(build: number | null | undefined, floor: number | null | undefined): boolean {
  if (build == null || floor == null) return false;
  if (!Number.isFinite(build) || !Number.isFinite(floor)) return false;
  return build < floor;
}

type Listener = (reason: UpdateRequiredReason) => void;
const listeners = new Set<Listener>();
let raised: UpdateRequiredReason | null = null;

export function onUpdateRequired(cb: Listener): () => void {
  listeners.add(cb);
  if (raised) cb(raised);
  return () => {
    listeners.delete(cb);
  };
}

export function raiseUpdateRequired(reason: UpdateRequiredReason): void {
  raised = reason;
  for (const cb of listeners) cb(reason);
}

/** The raised state, if any: a blocking layer the back button and the app lock defer to. */
export function getUpdateRequired(): UpdateRequiredReason | null {
  return raised;
}

/** Test-only: forget a raised state. */
export function resetUpdateRequired(): void {
  raised = null;
}

export interface VersionGuardConfig {
  /** The installed build's versionCode; null on web or before it is known. */
  getBuild: () => number | null;
  /** Latest cached floor, or null when unknown. */
  getFloor: () => number | null;
  /** Re-reads the floor from the server; resolves null when it cannot. */
  refreshFloor: () => Promise<number | null>;
  /** How long the refresh may take before the guard falls back to the cache. */
  refreshTimeoutMs?: number;
}

let config: VersionGuardConfig | null = null;

export function configureVersionGuard(next: VersionGuardConfig | null): void {
  config = next;
}

function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(fallback), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      () => {
        clearTimeout(t);
        resolve(fallback);
      },
    );
  });
}

/**
 * Resolves when the build is at or above a KNOWN floor, or when the floor is
 * unknown (fail open). Rejects — after raising the blocking screen — when the
 * build is below a known floor. Call it immediately before a
 * compatibility-sensitive RPC.
 */
export async function ensureVersionSupported(): Promise<void> {
  if (!config) return; // web, or the shell is not mounted: nothing to compare
  const build = config.getBuild();
  if (build == null) return;

  const fresh = await withTimeout(config.refreshFloor(), config.refreshTimeoutMs ?? 3000, null);
  const floor = fresh ?? config.getFloor();

  if (isBelowFloor(build, floor)) {
    raiseUpdateRequired('below-floor');
    throw new UpdateRequiredError('below-floor');
  }
}

/**
 * The Play listing of THIS build's package. Opened by a plain navigation from
 * the blocking screen: Capacitor hands any off-origin URL to Intent.ACTION_VIEW
 * (Bridge.java:389-419) and Android resolves it to the Play app.
 */
export function playStoreUrl(appId: string): string {
  return `https://play.google.com/store/apps/details?id=${encodeURIComponent(appId)}`;
}
